import { Language } from "@/types/language.types";

type RecoverPasswordRedirectProps = {
  redirect: string;
  lang: Language;
  email?: string;
};
class RouteUrls {
  private redirect = (path: string): string => `/?redirect=${path}`;

  public baseUrl = "/";

  // --------- DASHBOARD ROUTES ---------
  public dashboardBase = "/dashboard";

  // Dashboard - Sales
  public salesDashboard = (lang: Language): string => `${this.dashboardBase}/sales/${lang}`;

  // Dashboard - Service
  public serviceDashboard = (lang: Language): string =>
    `${this.dashboardBase}/service/operational/${lang}`;
  public serviceDashboardLegacy = (lang: Language): string =>
    `${this.dashboardBase}/service/${lang}`;

  public serviceManagerialDashboard = (lang: Language): string =>
    `${this.dashboardBase}/service/managerial/${lang}`;

  // ------------------------------------ //

  // --------- REGISTER ROUTES ---------
  public registerBase = "/register";

  public register = (lang: Language): string => `${this.registerBase}/${lang}`;
  public registerRedirect = (redirect: string, lang: Language) =>
    `${this.register(lang)}${this.redirect(redirect)}`;

  // --------- LOGIN ROUTES ---------
  public loginBase = "/login";

  public login = (lang: Language): string => `${this.loginBase}/${lang}`;
  public loginRedirect = (redirect: string, lang: Language): string => {
    const loginUrl = this.login(lang);

    const redirectUrl = `${loginUrl}?redirect=${redirect}`;

    return redirectUrl;
  };

  public recoverPasswordBase = `${this.loginBase}/recover-password`;

  public recoverPasswordLang = (lang: Language) => ({
    pathname: this.recoverPasswordBase,
    query: lang,
  });
  public recoverPassword = (lang: Language): string => `${this.recoverPasswordBase}/${lang}`;
  public recoverPasswordRedirect = ({ redirect, lang }: RecoverPasswordRedirectProps) =>
    `${this.recoverPassword(lang)}${this.redirect(redirect)}`;

  // --------- PLANS ROUTES ---------
  public plansBase = "/plans";

  public plans = (lang: Language): string => `${this.plansBase}/${lang}`;

  // --------- PROFILE ROUTES ---------

  public profileBase = "/profile";
  public permissionsBase = `${this.profileBase}/permissions`;
  public changePasswordBase = `${this.profileBase}/change-password`;
  public changePasswordExpiredBase = `${this.profileBase}/change-password-expired`;

  public profile = (lang: Language): string => `${this.profileBase}/${lang}`;
  public permissions = (lang: Language): string => `${this.permissionsBase}/${lang}`;
  public changePassword = (lang: Language): string => `${this.changePasswordBase}/${lang}`;
  public changePasswordExpired = (lang: Language, isPasswordExpired: boolean): string =>
    `${this.changePasswordExpiredBase}/${isPasswordExpired}/${lang}`;

  // --------- ORDERS ROUTES ---------

  public baseOrders = "/orders/orders";
  public baseOrderDetails = `${this.baseOrders}/details`;
  public baseOrderEdit = `${this.baseOrders}/edit`;
  public baseOrderPrint = `${this.baseOrders}/print`;
  public baseOrderCreate = `${this.baseOrders}/create`;

  public orders = (lang: Language): string => `${this.baseOrders}/${lang}`;
  public orderDetails = (lang: Language, orderId: string): string =>
    `${this.baseOrderDetails}/${orderId}/${lang}`;
  public orderEdit = (lang: Language, orderId: string): string =>
    `${this.baseOrderEdit}/${orderId}/${lang}`;
  public orderPrint = (lang: Language, orderId: string): string =>
    `${this.baseOrderPrint}/${orderId}/${lang}`;
  public createOrder = (lang: Language): string => `${this.baseOrderCreate}/${lang}`;

  // --------- CONTACTS ROUTES ---------

  public baseContacts = "/customerService/contacts";
  public baseCreateContact = `${this.baseContacts}/create`;
  public baseUpdateContact = `${this.baseContacts}/update`;

  public contacts = (lang: Language): string => `${this.baseContacts}/${lang}`;
  public createContact = (lang: Language): string => `${this.baseCreateContact}/${lang}`;
  public updateContact = (lang: Language, contactId: number): string =>
    `${this.baseUpdateContact}/${contactId}/${lang}`;

  // --------- PRODUCTS ROUTES ---------

  public baseProducts = "/catalog/products";
  public baseCreateProduct = `${this.baseProducts}/create`;
  public baseUpdateProduct = `${this.baseProducts}/update`;

  public products = (lang: Language): string => `${this.baseProducts}/${lang}`;
  public createProduct = (lang: Language): string => `${this.baseCreateProduct}/${lang}`;

  public updateProduct = (lang: Language, productId: number): string =>
    `${this.baseUpdateProduct}/${productId}/${lang}`;

  // --------- PRODUCTSCATEGORY ROUTES ---------

  public baseProductCategories = "/catalog/productCategories";
  public baseCreateProductCategory = `${this.baseProductCategories}/create`;
  public updateProductCategoryBase = `${this.baseProductCategories}/update`;

  public productCategories = (lang: Language): string => `${this.baseProductCategories}/${lang}`;
  public createProductCategory = (lang: Language): string =>
    `${this.baseCreateProductCategory}/${lang}`;
  public updateProductCategory = (lang: Language, categoryId: number): string =>
    `${this.updateProductCategoryBase}/${categoryId}/${lang}`;

  // --------- PRODUCTSCATEGORY ROUTES ---------

  // --------- PRODUCT VARIATIONS ROUTES ---------

  public baseVariations = "/catalog/productVariations";
  public baseCreateVariation = `${this.baseVariations}/create`;
  public updateVariationBase = `${this.baseVariations}/update`;

  public variations = (lang: Language): string => `${this.baseVariations}/${lang}`;
  public createProductVariation = (lang: Language): string => `${this.baseCreateVariation}/${lang}`;
  public updateProductVariation = (lang: Language, id: number): string =>
    `${this.updateVariationBase}/${id}/${lang}`;

  // --------- SKUs ROUTES ---------

  public baseSku = "/sku";
  public baseSkuDetails = `${this.baseSku}/details`;

  public skuDetails = (lang: Language, productId: number, skuId: number): string =>
    `${this.baseSkuDetails}/${productId}/${skuId}/${lang}`;
  public createSKU = (lang: Language, productId: number, productCode: string): string =>
    `${this.baseSku}/create/${productId}/${productCode}/${lang}`;

  // --------- CHECKOUT ROUTES ---------

  // --------- SETTINGS ROUTES ---------

  public baseSettings = "/settings";

  public settings = (lang: Language): string => `${this.baseSettings}/${lang}`;

  // --------- CAMPAIGNS ROUTES ---------

  public baseCampaigns = "/pricing/campaigns";
  public baseCreateCampaign = `${this.baseCampaigns}/create`;
  public baseUpdateCampaign = `${this.baseCampaigns}/update`;

  public campaigns = (lang: Language): string => `${this.baseCampaigns}/${lang}`;
  public createCampaign = (lang: Language): string => `${this.baseCreateCampaign}/${lang}`;
  public updateCampaign = (lang: Language, campaignId?: string): string =>
    `${this.baseUpdateCampaign}/${campaignId}/${lang}`;
  public campaignStatistics = (lang: Language, campaignId: string): string =>
    `${this.baseCampaigns}/statistics/${campaignId}/${lang}`;
  public campaignStatisticsDetails = (
    lang: Language,
    campaignId: string,
    scheduleDate: string
  ): string => `${this.baseCampaigns}/statistics/details/${campaignId}/${scheduleDate}/${lang}`;

  // --------- CAMPAIGNS ROUTES ---------

  // --------- CHAT ROUTES ---------

  public baseChat = "/customerService/chat";
  public chat = (lang: Language): string => `${this.baseChat}/${lang}`;
  public baseConversationReport = "/customerService/conversations";
  public conversationReport = (lang: Language): string => `${this.baseConversationReport}/${lang}`;

  // --------- NOTIFICATION CENTER ROUTES ---------
  public baseNotifications = "/notifications";
  public notifications = (lang: Language): string => `${this.baseNotifications}/${lang}`;

  // --------- IMPORTATION ROUTES ---------
  public baseImport = "/imports";
  public importations = (lang: Language): string => `${this.baseImport}/${lang}`;
  public importationDetails = (lang: Language, id: string): string =>
    `${this.baseImport}/details/${id}/${lang}`;

  // --------- USERS ROUTES ---------

  public baseUsers = "/settings/users";
  public baseCreateUser = `${this.baseUsers}/create`;
  public updateUserBase = `${this.baseUsers}/update`;

  public users = (lang: Language): string => `${this.baseUsers}/${lang}`;
  public createUser = (lang: Language): string => `${this.baseCreateUser}/${lang}`;
  public updateUser = (lang: Language, userId: number): string =>
    `${this.updateUserBase}/${userId}/${lang}`;

  // --------- CUSTOMER ROUTES ---------

  public baseCustomers = "/settings/company";

  public customer = (lang: Language): string => `${this.baseCustomers}/${lang}`;
  public customerSubscriptionTab = (lang: Language): string =>
    `${this.baseCustomers}/${lang}?tab=subscriptionManagementTab`;
  public companyPayments = (lang: Language): string => `${this.baseCustomers}/payments/${lang}`;

  // --------- PLATFORM ROUTES ---------
  public basePlatform = "/settings/platforms";

  public platform = (lang: Language): string => `${this.basePlatform}/${lang}`;
  public whatsappConnection = (lang: Language): string => `${this.basePlatform}/whatsapp/${lang}`;
  public t2PayConnection = (lang: Language): string => `${this.basePlatform}/talk2pay/${lang}`;
  public t2PayHome = (lang: Language): string => `${this.basePlatform}/talk2pay/home/${lang}`;
  public lojaIntegrada = (lang: Language): string => `${this.basePlatform}/loja-integrada/${lang}`;

  public generalInformation = (lang: Language): string =>
    `${this.basePlatform}/talk2pay/home/general-information/${lang}`;

  public documents = (lang: Language): string =>
    `${this.basePlatform}/talk2pay/home/documents/${lang}`;
  public rates = (lang: Language): string => `${this.basePlatform}/talk2pay/home/rates/${lang}`;
  public statement = (lang: Language): string =>
    `${this.basePlatform}/talk2pay/home/statement/${lang}`;

  // Nova Rota: Situação Cadastral
  public cadastralSituation = (lang: Language): string =>
    `${this.basePlatform}/talk2pay/home/cadastral-situation/${lang}`;

  // --------- WORKFLOW ROUTES ---------

  public baseWorkflows = "/settings/workflow";
  public baseCreateWorkflow = `${this.baseWorkflows}/create`;
  public updateWorkflowBase = `${this.baseWorkflows}/update`;

  public workflow = (lang: Language): string => `${this.baseWorkflows}/${lang}`;
  public createWorkflow = (lang: Language): string => `${this.baseCreateWorkflow}/${lang}`;
  public updateWorkflow = (lang: Language, workflowId: number): string =>
    `${this.updateWorkflowBase}/${workflowId}/${lang}`;
  public flowWorkflow = (lang: Language, workflowId: number): string =>
    `${this.baseWorkflows}/flow/${workflowId}/${lang}`;

  // --------- EMAIL VALIDATION ROUTES ---------
  public emailValidationBase = "/emailValidation";

  public emailValidation = (lang: Language): string => `${this.emailValidationBase}/${lang}`;
  public emailValidationRedirect = (redirect: string, lang: Language) =>
    `${this.emailValidation(lang)}${this.redirect(redirect)}`;

  // --------- EMAIL CONFIRMATION ROUTES ---------
  public emailConfirmationBase = "/emailConfirmation";

  public emailConfirmation = (lang: Language, token: string): string =>
    `${this.emailConfirmationBase}/${token}/${lang}`;
  public emailConfirmationRedirect = (redirect: string, lang: Language, token: string) =>
    `${this.emailConfirmation(lang, token)}${this.redirect(redirect)}`;

  // --------- TERMS ROUTE ---------
  public termsBase = "/terms";
  public terms = (lang: Language): string => `${this.termsBase}/${lang}`;

  // --------- PRIVACY ROUTE ---------
  public privacyBase = "/privacy";
  public privacy = (lang: Language): string => `${this.privacyBase}/${lang}`;

  // --------- SUBSCRIBE ROUTE ---------
  public baseSubscribe = "/subscribe";

  public subscribe = (lang: Language, salesproductpriceId: number): string =>
    `${this.baseSubscribe}/${salesproductpriceId}/${lang}`;

  // --------- LIST PRICE ROUTE ---------
  public listPriceBase = "/pricing/listPrice";
  public listPrice = (lang: Language): string => `${this.listPriceBase}/${lang}`;
  public updateListPrice = (lang: Language, listPriceId: number): string =>
    `${this.listPriceBase}/update/${listPriceId}/${lang}`;

  // --------- COVERAGE AREA ROUTES ---------

  public baseCoverageArea = "/settings/coverage-area";
  public baseCreateCoverageArea = `${this.baseCoverageArea}/create`;
  public baseUpdateCoverageArea = `${this.baseCoverageArea}/update`;

  public coverageArea = (lang: Language): string => `${this.baseCoverageArea}/${lang}`;
  public createCoverageArea = (lang: Language): string => `${this.baseCreateCoverageArea}/${lang}`;
  public updateCoverageArea = (lang: Language, areaId: number): string =>
    `${this.baseUpdateCoverageArea}/${areaId}/${lang}`;

  // --------- SERVICE QUEUE ROUTES ---------

  public baseServiceQueue = "/settings/service-queue";
  public baseCreateServiceQueue = `${this.baseServiceQueue}/create`;
  public baseUpdateServiceQueue = `${this.baseServiceQueue}/update`;
  public baseViewServiceQueue = `${this.baseServiceQueue}/view`;

  public serviceQueue = (lang: Language): string => `${this.baseServiceQueue}/${lang}`;
  public createServiceQueue = (lang: Language): string => `${this.baseCreateServiceQueue}/${lang}`;
  public updateServiceQueue = (lang: Language, queueId: number): string =>
    `${this.baseUpdateServiceQueue}/${queueId}/${lang}`;
  public viewServiceQueue = (lang: Language, queueId: number): string =>
    `${this.baseViewServiceQueue}/${queueId}/${lang}`;

  public baseSubjects = "/settings/subjects";
  public baseCreateSubject = `${this.baseSubjects}/create`;
  public baseUpdateSubject = `${this.baseSubjects}/update`;
  public baseViewSubject = `${this.baseSubjects}/view`;

  public subjects = (lang: Language): string => `${this.baseSubjects}/${lang}`;
  public createSubject = (lang: Language): string => `${this.baseCreateSubject}/${lang}`;
  public updateSubject = (lang: Language, subjectId: number): string =>
    `${this.baseUpdateSubject}/${subjectId}/${lang}`;
  public viewSubject = (lang: Language, subjectId: number): string =>
    `${this.baseViewSubject}/${subjectId}/${lang}`;

  // --------- MESSAGE TEMPLATES ROUTES ---------

  public baseMessageTemplates = "/settings/templates";
  public baseUpdateMessageTemplate = `${this.baseMessageTemplates}/update`;

  public messageTemplates = (lang: Language): string => `${this.baseMessageTemplates}/${lang}`;
  public updateMessageTemplate = (lang: Language, templateId: number): string =>
    `${this.baseUpdateMessageTemplate}/${templateId}/${lang}`;

  // --------- INTEGRATIONS ROUTES ---------

  public baseIntegrations = "/settings/integrations";

  public integrations = (lang: Language): string => `${this.baseIntegrations}/${lang}`;
  public integrationLogs = (lang: Language, serviceInterfaceId: number | string): string =>
    `${this.baseIntegrations}/${serviceInterfaceId}/logs/${lang}`;

  // --------- RENEW ROUTE ---------

  public renewBase = "/renew";
  public renew = (lang: Language, salesproductpriceId: number): string =>
    `${this.renewBase}/${salesproductpriceId}/${lang}`;
}

const ROUTE_URLS = new RouteUrls();
export default ROUTE_URLS;

export const UNPROTECTED_ROUTE = [ROUTE_URLS.loginBase, ROUTE_URLS.termsBase];
